import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NxMessageToastService } from '@aposin/ng-aquila/message';
import { SortDirection, SortEvent } from '@aposin/ng-aquila/table';
import { AppConfig } from 'src/config/app.config';
import { AuthService } from '../core/auth/auth.service';
import * as moment from 'moment';

@Component({
  selector: 'app-admin-commission-invoice',
  templateUrl: './admin-commission-invoice.component.html',
  styleUrls: ['./admin-commission-invoice.component.css']
})
export class AdminCommissionInvoiceComponent implements OnInit {

  private _apiUrl: string;

  count: number = 0;
  pageNumber: number = 1;
  pageSize: number = 10;

  invoiceList: any[] = [];
  searchTerm: string = '';
  startDate: moment.Moment | null = null;
  endDate: moment.Moment | null = null;
  exportData: any[] = [];
  fileName: string = '';

  readonly months: string[] = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  statusFilter: string = 'All';

  reviewModalOpen: boolean = false;
  reviewMode: 'approve' | 'reject' = 'approve';
  reviewForm!: FormGroup;
  reviewInProgress: boolean = false;
  selectedInvoice: any = null;

  paidModalOpen: boolean = false;
  paidForm!: FormGroup;
  paidInProgress: boolean = false;

  statusModalOpen: boolean = false;
  statusInProgress: boolean = false;

  detailsModalOpen: boolean = false;

  openDetailsModal(item: any) {
    this.selectedInvoice = item;
    this.detailsModalOpen = true;
    this.authService
      .get(this._apiUrl + `/api/v1/CommissionInvoice/details/${item.id}`)
      .subscribe(
        (response) => { if (response) this.selectedInvoice = response; },
        (error) => console.error(error)
      );
  }

  closeDetailsModal() {
    this.detailsModalOpen = false;
    this.selectedInvoice = null;
  }

  prevPage() { this.pageNumber--; this.getInvoiceList(); }
  nextPage() { this.pageNumber++; this.getInvoiceList(); }
  goToPage(n: number) { this.pageNumber = n; this.getInvoiceList(); }

  sortTable(sort: SortEvent) {
    this.invoiceList = this.invoiceList.sort((a, b) =>
      sort.active in a && sort.active in b ? this.compare(a[sort.active], b[sort.active], sort.direction) : 0
    );
  }

  compare(a: number | string | Date, b: number | string | Date, direction: SortDirection) {
    return (a < b ? -1 : 1) * (direction === 'asc' ? 1 : -1);
  }

  constructor(
    private authService: AuthService,
    appConfig: AppConfig,
    private fb: FormBuilder,
    private messageToastService: NxMessageToastService
  ) {
    this._apiUrl = appConfig.baseUrl;
  }

  ngOnInit(): void {
    this.getInvoiceList();
  }

  getInvoiceList() {
    const statusParam = this.statusFilter === 'All' ? '' : this.statusFilter;
    const from = this.startDate ? this.startDate.format('LL') : '';
    const to = this.endDate ? this.endDate.format('LL') : '';
    this.authService
      .getList(
        this._apiUrl +
        `/api/v1/CommissionInvoice/admin/list?pageNumber=${this.pageNumber}&pageSize=${this.pageSize}` +
        `&searchText=${this.searchTerm}&status=${statusParam}&startDate=${from}&endDate=${to}`
      )
      .subscribe(
        (response) => {
          if (response) {
            this.invoiceList = response.body || [];
            const paging = JSON.parse(response.headers.get('X-Pagination') || '{"TotalCount":0}');
            this.count = paging.TotalCount;
          }
        },
        (error) => console.error(error)
      );
  }

  exportToExcel() {
    const statusParam = this.statusFilter === 'All' ? '' : this.statusFilter;
    const from = this.startDate ? this.startDate.format('LL') : '';
    const to = this.endDate ? this.endDate.format('LL') : '';
    this.authService
      .getList(
        this._apiUrl +
        `/api/v1/CommissionInvoice/admin/list?pageNumber=-1&pageSize=10000` +
        `&searchText=${this.searchTerm}&status=${statusParam}&startDate=${from}&endDate=${to}`
      )
      .subscribe(
        (response) => {
          if (response) {
            const rows = (response.body || []).map((item: any) => ({
              'Dealer': item.dealerName || '',
              'Invoice Period': `${this.months[item.invoiceMonth - 1]} ${item.invoiceYear}`,
              'Submitted On': item.submittedOn ? new Date(item.submittedOn).toLocaleDateString('en-GB') : '',
              'Dealer Comments': item.comments || '',
              'Status': item.status || ''
            }));
            import('xlsx').then(xlsx => {
              const ws = xlsx.utils.json_to_sheet(rows);
              const wb = xlsx.utils.book_new();
              xlsx.utils.book_append_sheet(wb, ws, 'Commission Invoices');
              this.fileName = `AdminCommissionInvoices_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.xlsx`;
              xlsx.writeFile(wb, this.fileName);
            });
          }
        },
        (error) => console.error(error)
      );
  }

  openReviewModal(mode: 'approve' | 'reject', item: any) {
    this.reviewMode = mode;
    this.selectedInvoice = item;
    this.reviewForm = this.fb.group({
      adminComments: [
        '',
        mode === 'reject'
          ? [Validators.required, Validators.maxLength(500)]
          : [Validators.maxLength(500)]
      ]
    });
    this.reviewModalOpen = true;
  }

  closeReviewModal() {
    this.reviewModalOpen = false;
    this.selectedInvoice = null;
  }

  submitReview() {
    this.reviewForm.markAllAsTouched();
    if (this.reviewForm.invalid) return;

    this.reviewInProgress = true;
    const payload = {
      id: this.selectedInvoice.id,
      status: this.reviewMode === 'approve' ? 'Invoice Accepted' : 'Invoice Rejected',
      adminComments: this.reviewForm.value.adminComments,
      rejectionReason: this.reviewMode === 'reject' ? this.reviewForm.value.adminComments : null
    };

    this.authService
      .put(this._apiUrl + `/api/v1/CommissionInvoice/review/${this.selectedInvoice.id}`, payload)
      .subscribe(
        () => {
          this.reviewInProgress = false;
          this.reviewModalOpen = false;
          const msg = this.reviewMode === 'approve' ? 'Invoice accepted successfully.' : 'Invoice rejected.';
          this.messageToastService.open(msg, { context: 'success', duration: 4000 });
          this.getInvoiceList();
        },
        (error) => {
          this.reviewInProgress = false;
          console.error(error);
          this.messageToastService.open('Action failed. Please try again.', { context: 'info', duration: 4000 });
        }
      );
  }

  openStatusModal(item: any) {
    this.selectedInvoice = item;
    this.statusModalOpen = true;
  }

  closeStatusModal() {
    this.statusModalOpen = false;
    this.selectedInvoice = null;
  }

  markUnderProcess() {
    this.statusInProgress = true;
    this.authService
      .put(this._apiUrl + `/api/v1/CommissionInvoice/review/${this.selectedInvoice.id}`,
        { id: this.selectedInvoice.id, status: 'Invoice Under Process' })
      .subscribe(
        () => {
          this.statusInProgress = false;
          this.statusModalOpen = false;
          this.messageToastService.open('Invoice marked as Under Process.', { context: 'success', duration: 4000 });
          this.getInvoiceList();
        },
        (error) => {
          this.statusInProgress = false;
          console.error(error);
          this.messageToastService.open('Action failed. Please try again.', { context: 'info', duration: 4000 });
        }
      );
  }

  openPaidModal(item: any) {
    this.selectedInvoice = item;
    this.paidForm = this.fb.group({
      utrNumber: ['', [Validators.required, Validators.maxLength(100)]],
      paymentDate: [null, Validators.required],
      amountPaid: ['', [Validators.required, Validators.pattern(/^\d+(\.\d{1,2})?$/)]],
      paymentRemarks: ['', Validators.maxLength(500)]
    });
    this.paidModalOpen = true;
  }

  closePaidModal() {
    this.paidModalOpen = false;
    this.selectedInvoice = null;
  }

  submitPaid() {
    this.paidForm.markAllAsTouched();
    if (this.paidForm.invalid) return;

    this.paidInProgress = true;
    const payload = {
      id: this.selectedInvoice.id,
      status: 'Invoice Paid',
      utrNumber: this.paidForm.value.utrNumber,
      paymentDate: this.paidForm.value.paymentDate,
      amountPaid: this.paidForm.value.amountPaid,
      paymentRemarks: this.paidForm.value.paymentRemarks
    };

    this.authService
      .put(this._apiUrl + `/api/v1/CommissionInvoice/markpaid/${this.selectedInvoice.id}`, payload)
      .subscribe(
        () => {
          this.paidInProgress = false;
          this.paidModalOpen = false;
          this.messageToastService.open('Invoice marked as Paid.', { context: 'success', duration: 4000 });
          this.getInvoiceList();
        },
        (error) => {
          this.paidInProgress = false;
          console.error(error);
          this.messageToastService.open('Action failed. Please try again.', { context: 'info', duration: 4000 });
        }
      );
  }

  downloadInvoice(invoiceId: number, referenceNo: string) {
    this.authService
      .export(this._apiUrl + `/api/v1/CommissionInvoice/download/${invoiceId}`)
      .subscribe((data) => saveAs(data, `${referenceNo}_commission_invoice.pdf`));
  }

  badgeType(status: string): string {
    switch (status) {
      case 'Invoice Accepted':
      case 'Invoice Paid': return 'positive';
      case 'Invoice Rejected': return 'critical';
      case 'Submitted':
      case 'Invoice Under Process': return 'active';
      default: return 'active';
    }
  }

  canReview(status: string): boolean {
    return status === 'Submitted';
  }

  canMarkUnderProcess(status: string): boolean {
    return status === 'Invoice Accepted';
  }

  canMarkPaid(status: string): boolean {
    return status === 'Invoice Under Process';
  }
}
